/**
 * ModerationAudioContent Component
 *
 * Displays multiple audio items in a semantic list layout.
 * Each item shows a label and an inline audio player with proper
 * aria-labelledby associations for screen reader accessibility.
 *
 * @example
 * ```tsx
 * <ModerationAudioContent
 *   audioItems={[
 *     { id: 'audio1', url: 'https://...', contentId: 'audio1', type: 'audio', label: 'Audio 1' },
 *     { id: 'audio2', url: 'https://...', contentId: 'audio2', type: 'audio' }
 *   ]}
 * />
 * ```
 */

import { InlineAudioPlayer } from '@core/components';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import type { MediaItem } from '@moderation/utils/contentDetection';
import type { ReactNode } from 'react';

import * as styles from './ModerationCaseCard.css';

/**
 * Props for ModerationAudioContent
 */
export interface ModerationAudioContentProps {
  /**
   * Array of audio items to display
   */
  audioItems: MediaItem[];
}

/**
 * ModerationAudioContent Component
 *
 * Renders multiple audio items in a semantic list with labels and inline audio players.
 * Uses the shared AudioPlayerProvider context for playback state management.
 */
export function ModerationAudioContent({ audioItems }: ModerationAudioContentProps): ReactNode {
  const { mediaServiceUrl } = useSystemConfig();

  if (!audioItems || audioItems.length === 0) {
    return null;
  }

  return (
    <ul className={styles.audioList}>
      {audioItems.map((item, index) => {
        const labelId = `audio-label-${item.id}`;

        return (
          <li key={item.id} className={styles.audioItem}>
            <span className={styles.audioLabel} id={labelId}>
              {item.label || `Audio ${index + 1}`}
            </span>
            <div aria-labelledby={labelId} className={styles.audioPlayer} role="group">
              <InlineAudioPlayer audioId={item.id} audioUrl={item.url} mediaServiceUrl={mediaServiceUrl} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

ModerationAudioContent.displayName = 'ModerationAudioContent';
